import type { ProviderEnum } from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../../../client/core/getCore';
import { getDefaultClient } from '../../../../../client/defaultClient';
import { createApiClient } from '../../../../apiClient';
import { removeDynamicOauthParamsFromUrl } from '../../removeDynamicOauthParamsFromUrl';
import type { SocialProvider } from '../../social.types';
import { redirectStateStorageKeySchema } from '../redirectStateStorageSchema';
import { buildOAuthUrl } from './buildOAuthUrl';

type AuthenticateWithSocialParams = {
  provider: SocialProvider;
  redirectUrl: string;
};

/**
 * Initiates social authentication by redirecting to the OAuth provider.
 *
 * This function redirects the user to the specified social provider's
 * authorization page to complete OAuth authentication flow.
 * After the oauth flow is complete, the user will be redirected back to your app.
 * You can then call `detectOAuthRedirect` to check if the user got redirected back to your app due to the oauth flow,
 * and finally call `completeSocialAuthentication` to complete the social authentication flow.
 *
 * @param params.provider - The social provider to authenticate with (e.g., 'google', 'github').
 * @param params.redirectUrl - The URL to redirect back to after authentication.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 */
export const authenticateWithSocial = async (
  { provider, redirectUrl }: AuthenticateWithSocialParams,
  client = getDefaultClient()
) => {
  const core = getCore(client);
  const apiClient = createApiClient({}, client);

  await core.initTrack.waitForAll();

  const providerType = provider as ProviderEnum;

  const { url, state, codeVerifier } = await buildOAuthUrl(core, providerType);

  await apiClient.initAuth({
    environmentId: core.environmentId,
    oauthInitAuthRequest: {
      redirectUrl: removeDynamicOauthParamsFromUrl(redirectUrl),
      state,
    },
    providerType,
  });

  await core.storage.setItem(redirectStateStorageKeySchema, {
    codeVerifier,
    provider: providerType,
    state,
  });

  await core.navigate(url.toString());
};
